import {
  PhoneIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';

import { Card, Spinner } from '@realvisor/ui';
import { api } from '@/lib/api';

interface Activity {
  id: string;
  type: string;
  subject: string;
  meta: Record<string, any>;
  createdAt: string;
  user: {
    id: string;
    fullName: string | null;
    avatarUrl: string | null;
  } | null;
}

export default function CallStats() {
  // Fetch all call activities (supervisor+ sees all)
  const { data: activitiesData, isLoading } = useQuery({
    queryKey: ['call-stats-activities'],
    queryFn: () =>
      api.get<{ data: Activity[]; total: number }>('/activities?type=call&limit=200'),
  });

  const activities = activitiesData?.data ?? [];

  // Calculate stats
  const today = new Date().toISOString().split('T')[0] as string;
  const todayCalls = activities.filter((a) => a.createdAt.startsWith(today));
  const totalDuration = todayCalls.reduce(
    (sum, a) => sum + (a.meta?.['duration'] ?? 0),
    0,
  );
  const completedCalls = todayCalls.filter(
    (a) => a.meta?.['callStatus'] === 'completed',
  );

  // Group by operator
  const operatorMap = new Map<string, { name: string; calls: number; duration: number }>();
  for (const activity of todayCalls) {
    const userId = activity.user?.id ?? 'unknown';
    const name = activity.user?.fullName ?? 'Neznámý';
    const existing = operatorMap.get(userId) ?? { name, calls: 0, duration: 0 };
    existing.calls += 1;
    existing.duration += activity.meta?.['duration'] ?? 0;
    operatorMap.set(userId, existing);
  }
  const operators = Array.from(operatorMap.values()).sort(
    (a, b) => b.calls - a.calls,
  );

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Call statistiky</h1>
        <p className="mt-1 text-sm text-gray-500">
          Přehled výkonnosti call centra
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-50 p-2">
              <PhoneIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Hovory dnes</p>
              <p className="text-2xl font-bold text-gray-900">{todayCalls.length}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-50 p-2">
              <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Spojené</p>
              <p className="text-2xl font-bold text-gray-900">{completedCalls.length}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-50 p-2">
              <ClockIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Celková doba</p>
              <p className="text-2xl font-bold text-gray-900">{formatDuration(totalDuration)}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-orange-50 p-2">
              <UserIcon className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Operátoři</p>
              <p className="text-2xl font-bold text-gray-900">{operators.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Operator leaderboard */}
      <Card padding="none">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">
            Výkon operátorů (dnes)
          </h2>
        </div>
        {operators.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            Dnes zatím žádné hovory
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500">
                <th className="px-4 py-2">Operátor</th>
                <th className="px-4 py-2 text-right">Hovory</th>
                <th className="px-4 py-2 text-right">Celk. doba</th>
                <th className="px-4 py-2 text-right">Prům. délka</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {operators.map((op, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{op.name}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700">{op.calls}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700">{formatDuration(op.duration)}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700">
                    {formatDuration(Math.round(op.duration / op.calls))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* All calls today */}
      <Card padding="none">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">
            Celkový přehled hovorů ({activities.length})
          </h2>
        </div>
        {activities.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            Žádné hovory
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500">
                  <th className="px-4 py-2">Čas</th>
                  <th className="px-4 py-2">Operátor</th>
                  <th className="px-4 py-2">Hovor</th>
                  <th className="px-4 py-2 text-right">Délka</th>
                  <th className="px-4 py-2 text-right">Směr</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {activities.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {new Date(a.createdAt).toLocaleString('cs-CZ', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {a.user?.fullName ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900">{a.subject}</td>
                    <td className="px-4 py-2 text-right text-sm text-gray-700">
                      {a.meta?.['duration'] != null ? formatDuration(a.meta['duration']) : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span
                        className={`text-xs font-medium ${
                          a.meta?.['callDirection'] === 'inbound'
                            ? 'text-blue-600'
                            : 'text-green-600'
                        }`}
                      >
                        {a.meta?.['callDirection'] === 'inbound' ? 'Příchozí' : 'Odchozí'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
